import { useState, useMemo, useCallback } from 'react';
import { Wallet, ArrowDownUp, TrendingUp, Info, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { CryptoStakingProps, CryptoAsset } from './CryptoStaking.types';
import { TENANT } from '../../config';

// ─────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ─────────────────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function formatCurrencyPrecise(amount: number): string {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function calculateUsdValue(cryptoAmount: string, availableCrypto: string, availableUsd: number): number {
  const amount = parseFloat(cryptoAmount || '0');
  const available = parseFloat(availableCrypto || '0');
  if (!Number.isFinite(amount) || !Number.isFinite(available) || available === 0) {
    return 0;
  }
  return (amount / available) * availableUsd;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function CryptoStaking({
  assets = [],
  networkFee = { amount: 2.50, isEstimate: true },
  onInvest,
  isInvesting = false,
  isLoading = false,
  error = null,
}: CryptoStakingProps) {
  const [isInvestModalOpen, setIsInvestModalOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string>(assets[0]?.symbol || '');
  const [amount, setAmount] = useState('');
  const [investmentStep, setInvestmentStep] = useState<'amount' | 'review' | 'success'>('amount');

  // Derived state
  const selectedAsset = useMemo(
    () => assets.find(asset => asset.symbol === selectedSymbol),
    [assets, selectedSymbol]
  );

  const totalCryptoValue = useMemo(
    () => assets.reduce((sum, asset) => sum + asset.usdValue, 0),
    [assets]
  );

  const totalStaked = useMemo(
    () => assets.reduce((sum, asset) => sum + asset.stakedUsd, 0),
    [assets]
  );

  const totalAvailable = useMemo(
    () => assets.reduce((sum, asset) => sum + asset.availableUsd, 0),
    [assets]
  );

  const averageApy = useMemo(() => {
    if (assets.length === 0) return 0;
    const totalApy = assets.reduce((sum, asset) => sum + asset.apy, 0);
    return totalApy / assets.length;
  }, [assets]);

  const convertedUsdValue = useMemo(() => {
    if (!selectedAsset) return 0;
    return calculateUsdValue(amount, selectedAsset.available, selectedAsset.availableUsd);
  }, [amount, selectedAsset]);

  const finalAmount = useMemo(() => {
    return Math.max(0, convertedUsdValue - networkFee.amount);
  }, [convertedUsdValue, networkFee.amount]);

  // Handlers
  const handleOpenInvestModal = useCallback((symbol?: string) => {
    if (symbol) {
      setSelectedSymbol(symbol);
    }
    setIsInvestModalOpen(true);
    setInvestmentStep('amount');
    setAmount('');
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsInvestModalOpen(false);
    setInvestmentStep('amount');
    setAmount('');
  }, []);

  const handleReview = useCallback(() => {
    const parsedAmount = parseFloat(amount);
    if (Number.isFinite(parsedAmount) && parsedAmount > 0) {
      setInvestmentStep('review');
    }
  }, [amount]);

  const handleConfirmInvestment = useCallback(async () => {
    if (!selectedAsset || !onInvest) {
      setInvestmentStep('success');
      setTimeout(handleCloseModal, 2500);
      return;
    }

    try {
      await onInvest({
        symbol: selectedAsset.symbol,
        amount,
        usdValue: convertedUsdValue,
        networkFee: networkFee.amount,
      });
      setInvestmentStep('success');
      setTimeout(handleCloseModal, 2500);
    } catch {
      // Error handling would be managed by parent
    }
  }, [selectedAsset, onInvest, amount, convertedUsdValue, networkFee.amount, handleCloseModal]);

  const handleAmountChange = useCallback((value: string) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  }, []);

  const handlePercentageClick = useCallback((percentage: number) => {
    const available = parseFloat(selectedAsset?.available || '0');
    if (Number.isFinite(available)) {
      setAmount((available * percentage).toFixed(4));
    }
  }, [selectedAsset?.available]);

  const isAmountValid = useMemo(() => {
    const parsedAmount = parseFloat(amount);
    const available = parseFloat(selectedAsset?.available || '0');
    return Number.isFinite(parsedAmount) && parsedAmount > 0 && parsedAmount <= available;
  }, [amount, selectedAsset?.available]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-primary" size={40} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <AlertCircle className="text-destructive" size={48} />
        <p className="text-muted-foreground text-center">{error}</p>
      </div>
    );
  }

  // Empty/Zero state - show card structure with placeholder values
  if (assets.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-display font-semibold mb-2">Crypto Assets</h2>
            <p className="text-muted-foreground">
              Use your cryptocurrency to invest in real estate properties
            </p>
          </div>
        </div>

        {/* Portfolio Overview Banner - Zero State */}
        <Card className="p-6 lg:p-8 border-border bg-[#F0F0F0]">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-2">Total Crypto Balance</p>
              <div className="flex items-baseline gap-3">
                <h2 className="text-4xl lg:text-5xl font-display font-semibold text-[#242424]">
                  $0.00
                </h2>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                No cryptocurrencies connected
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Total Staked</p>
                <p className="text-xl font-semibold text-[#242424]">$0</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Available</p>
                <p className="text-xl font-semibold text-[#242424]">$0</p>
              </div>
              <div className="col-span-2 lg:col-span-1">
                <p className="text-xs text-muted-foreground mb-1">Avg. APY</p>
                <p className="text-xl font-semibold text-muted-foreground">--</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Empty Assets Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-display font-semibold text-[#242424]">Your Assets</h3>
              <p className="text-sm text-muted-foreground">0 cryptocurrencies</p>
            </div>
            <Button onClick={() => handleOpenInvestModal()} className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Wallet className="w-4 h-4 mr-2" />
              Invest with Crypto
            </Button>
          </div>

          {/* Empty State Card */}
          <Card className="p-8 border-border border-dashed">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-16 h-16 bg-[#F0F0F0] rounded-full flex items-center justify-center">
                <Wallet className="text-muted-foreground" size={32} />
              </div>
              <div>
                <h4 className="text-base font-semibold text-[#242424] mb-1">No Crypto Assets</h4>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Connect your wallet or deposit cryptocurrency to start investing in real estate with crypto.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="p-4 border-border bg-primary/5">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-[#242424] mb-1">How Crypto Investment Works</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Convert your cryptocurrency to USD and invest directly in real estate properties. Your crypto is
                converted at market rate with low network fees. Minimum investment: $100 USD equivalent. Transactions
                typically complete within minutes.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-display font-semibold mb-2">Crypto Assets</h2>
            <p className="text-muted-foreground">
              Use your cryptocurrency to invest in real estate properties
            </p>
          </div>
        </div>

        {/* Portfolio Overview Banner */}
        <Card className="p-6 lg:p-8 border-border bg-[#F0F0F0]">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-2">Total Crypto Balance</p>
              <div className="flex items-baseline gap-3">
                <h2 className="text-4xl lg:text-5xl font-display font-semibold text-[#242424]">
                  {formatCurrencyPrecise(totalCryptoValue)}
                </h2>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Across {assets.length} cryptocurrenc{assets.length === 1 ? 'y' : 'ies'}
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Total Staked</p>
                <p className="text-xl font-semibold text-[#242424]">
                  {formatCurrency(totalStaked)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Available</p>
                <p className="text-xl font-semibold text-[#242424]">
                  {formatCurrency(totalAvailable)}
                </p>
              </div>
              <div className="col-span-2 lg:col-span-1">
                <p className="text-xs text-muted-foreground mb-1">Avg. APY</p>
                <p className="text-xl font-semibold text-[hsl(var(--success))]">
                  {formatPercent(averageApy)}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Crypto Assets List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-display font-semibold text-[#242424]">Your Assets</h3>
              <p className="text-sm text-muted-foreground">{assets.length} cryptocurrenc{assets.length === 1 ? 'y' : 'ies'}</p>
            </div>
            <Button onClick={() => handleOpenInvestModal()} className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Wallet className="w-4 h-4 mr-2" />
              Invest with Crypto
            </Button>
          </div>

          <div className="space-y-4">
            {assets.map((asset) => (
              <CryptoAssetCard
                key={asset.symbol}
                asset={asset}
                onInvest={() => handleOpenInvestModal(asset.symbol)}
              />
            ))}
          </div>
        </div>

        {/* Info Card */}
        <Card className="p-4 border-border bg-primary/5">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-[#242424] mb-1">How Crypto Investment Works</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Convert your cryptocurrency to USD and invest directly in real estate properties. Your crypto is
                converted at market rate with low network fees. Minimum investment: $100 USD equivalent. Transactions
                typically complete within minutes.
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Investment Modal */}
      <Dialog open={isInvestModalOpen} onOpenChange={setIsInvestModalOpen}>
        <DialogContent className="sm:max-w-lg">
          {investmentStep === 'amount' && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Invest with Crypto</DialogTitle>
                <DialogDescription>
                  Convert {selectedAsset?.name} to invest in real estate
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 mt-4">
                {/* Crypto Selector */}
                <div>
                  <label className="text-sm font-medium mb-2 block text-[#242424]">Select Cryptocurrency</label>
                  <Tabs value={selectedSymbol} onValueChange={setSelectedSymbol}>
                    <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${assets.length}, 1fr)` }}>
                      {assets.map((asset) => (
                        <TabsTrigger key={asset.symbol} value={asset.symbol}>
                          {asset.symbol}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                </div>

                {/* Available Balance */}
                <Card className="p-4 bg-[#F0F0F0] border-border">
                  <p className="text-xs text-muted-foreground mb-1">Available Balance</p>
                  <p className="text-base font-semibold text-[#242424]">
                    {selectedAsset?.available} {selectedSymbol}
                    <span className="text-sm font-normal text-muted-foreground ml-2">
                      ({formatCurrencyPrecise(selectedAsset?.availableUsd || 0)})
                    </span>
                  </p>
                </Card>

                {/* Amount Input */}
                <div>
                  <label htmlFor="crypto-amount-input" className="text-sm font-medium mb-2 block text-[#242424]">Amount to Invest</label>
                  <div className="relative">
                    <input
                      id="crypto-amount-input"
                      type="text"
                      inputMode="decimal"
                      value={amount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-4 py-3 pr-16 rounded-lg border border-border bg-background text-[#242424] focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium pointer-events-none">
                      {selectedSymbol}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => handlePercentageClick(0.25)}>
                      25%
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => handlePercentageClick(0.5)}>
                      50%
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => handlePercentageClick(0.75)}>
                      75%
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => handlePercentageClick(1)}>
                      Max
                    </Button>
                  </div>
                </div>

                {/* Conversion Preview */}
                {amount && parseFloat(amount) > 0 && (
                  <Card className="p-4 bg-primary/5 border-primary/20">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">You invest</span>
                        <span className="font-semibold text-[#242424]">{amount} {selectedSymbol}</span>
                      </div>
                      <div className="flex items-center justify-center">
                        <ArrowDownUp className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">You receive</span>
                        <span className="font-semibold text-[#242424]">
                          ~{formatCurrencyPrecise(convertedUsdValue)} USD
                        </span>
                      </div>
                      <div className="border-t border-primary/20 pt-3">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">
                            Network fee{networkFee.isEstimate ? ' (est.)' : ''}
                          </span>
                          <span className="text-muted-foreground">~{formatCurrencyPrecise(networkFee.amount)}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}

                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="flex-1" onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={handleReview}
                    disabled={!isAmountValid}
                  >
                    Review Investment
                  </Button>
                </div>
              </div>
            </>
          )}

          {investmentStep === 'review' && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Review Investment</DialogTitle>
                <DialogDescription>
                  Confirm the details before proceeding
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 mt-4">
                <Card className="p-5 bg-[#F0F0F0] border-border">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Cryptocurrency</span>
                      <span className="font-semibold text-[#242424]">{selectedAsset?.name} ({selectedSymbol})</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Amount</span>
                      <span className="font-semibold text-[#242424]">{amount} {selectedSymbol}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">USD Value</span>
                      <span className="font-semibold text-[#242424]">{formatCurrencyPrecise(convertedUsdValue)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Network Fee</span>
                      <span className="font-semibold text-[#242424]">{formatCurrencyPrecise(networkFee.amount)}</span>
                    </div>
                    <div className="border-t border-border pt-3 flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#242424]">Total to Receive</span>
                      <span className="text-base font-semibold text-[#242424]">{formatCurrencyPrecise(finalAmount)}</span>
                    </div>
                  </div>
                </Card>

                <Card className="p-4 bg-primary/5 border-primary/20">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Your funds will be available in your {TENANT.branding.name} account immediately after confirmation.
                    You can invest in any available property or hold as USD balance.
                  </p>
                </Card>

                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1" onClick={() => setInvestmentStep('amount')}>
                    Back
                  </Button>
                  <Button
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={handleConfirmInvestment}
                    disabled={isInvesting}
                  >
                    {isInvesting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Confirm Investment'
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}

          {investmentStep === 'success' && (
            <>
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <DialogTitle className="mb-2 font-display">Investment Successful!</DialogTitle>
                <DialogDescription className="mb-6">
                  Your {selectedSymbol} has been converted and added to your account
                </DialogDescription>

                <Card className="p-5 bg-[#F0F0F0] border-border text-left">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Converted Amount</span>
                      <span className="font-semibold text-[#242424]">{formatCurrencyPrecise(finalAmount)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Status</span>
                      <span className="font-semibold text-[hsl(var(--success))]">Available Now</span>
                    </div>
                  </div>
                </Card>

                <p className="text-xs text-muted-foreground mt-6">
                  Browse properties to start investing
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface CryptoAssetCardProps {
  asset: CryptoAsset;
  onInvest: () => void;
}

function CryptoAssetCard({ asset, onInvest }: CryptoAssetCardProps) {
  return (
    <Card className="overflow-hidden border-border hover:border-[hsl(var(--chart-2))]/30 transition-all duration-200 group">
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#1944FF]/10 flex items-center justify-center flex-shrink-0">
              <span className="text-base font-semibold text-[#1944FF]">{asset.symbol}</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#242424] mb-0.5">{asset.name}</h3>
              <p className="text-sm text-muted-foreground">{asset.symbol}</p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-base font-semibold text-[#242424]">{asset.balance} {asset.symbol}</p>
            <p className="text-sm text-muted-foreground">{formatCurrencyPrecise(asset.usdValue)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Available */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Available</p>
            <p className="text-base font-semibold text-[#242424]">{asset.available}</p>
            <p className="text-xs text-muted-foreground">{formatCurrencyPrecise(asset.availableUsd)}</p>
          </div>

          {/* Staked */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Staked</p>
            <p className="text-base font-semibold text-[#242424]">{asset.staked}</p>
            <p className="text-xs text-muted-foreground">{formatCurrencyPrecise(asset.stakedUsd)}</p>
          </div>

          {/* APY */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Staking APY</p>
            <div className="flex items-center gap-1.5 text-[hsl(var(--success))]">
              <TrendingUp className="w-4 h-4" />
              <span className="text-base font-semibold">{formatPercent(asset.apy)}</span>
            </div>
          </div>

          {/* Action */}
          <div className="flex items-end justify-end">
            <Button size="sm" onClick={onInvest}>
              Invest
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
